import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Pause, Music, Volume2, VolumeX, SkipBack, SkipForward, Clock, Activity, Share2, Check, ListMusic } from 'lucide-react';
import { toast, Toaster } from 'sonner';

// Use runtime origin to avoid stale build-time env var pointing to wrong host
const API_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

const PublicPlaylist = () => {
  const { playlistId } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Player state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchPlaylist();
  }, [playlistId]);

  const fetchPlaylist = async () => {
    try {
      const res = await fetch(`${API_URL}/api/playlists/${playlistId}`);
      if (!res.ok) { setError('Playlist not found'); return; }
      const data = await res.json();
      setPlaylist(data.playlist);
    } catch {
      setError('Failed to load playlist');
    } finally {
      setLoading(false);
    }
  };

  const tracks = playlist?.tracks || [];

  const playTrack = (track) => {
    if (currentTrack?.id === track.id) {
      if (isPlaying) { audioRef.current?.pause(); setIsPlaying(false); }
      else { audioRef.current?.play(); setIsPlaying(true); }
    } else {
      setCurrentTrack(track);
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (currentTrack && audioRef.current) {
      audioRef.current.src = `${API_URL}${currentTrack.audio_url}`;
      audioRef.current.volume = isMuted ? 0 : volume;
      audioRef.current.play().catch(console.error);
    }
  }, [currentTrack]);

  const playNext = () => {
    const idx = tracks.findIndex(t => t.id === currentTrack?.id);
    if (idx < tracks.length - 1) { setCurrentTrack(tracks[idx + 1]); setIsPlaying(true); }
  };

  const playPrev = () => {
    const idx = tracks.findIndex(t => t.id === currentTrack?.id);
    if (idx > 0) { setCurrentTrack(tracks[idx - 1]); setIsPlaying(true); }
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    toast.success('Playlist link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const totalDuration = tracks.reduce((sum, t) => sum + (t.duration || 0), 0);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-black flex items-center justify-center text-center px-4">
      <div>
        <ListMusic size={64} className="mx-auto text-gray-700 mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Playlist Not Found</h2>
        <p className="text-gray-400">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <Toaster position="top-center" theme="dark" />

      <audio
        ref={audioRef}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
            setDuration(audioRef.current.duration || 0);
          }
        }}
        onEnded={playNext}
        onLoadedMetadata={() => {
          if (audioRef.current) setDuration(audioRef.current.duration || 0);
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-white/20 to-white/5 border border-white/20 flex items-center justify-center">
              <Music size={18} className="text-white" />
            </div>
            <span className="font-bold text-white tracking-widest text-sm">CREATRVERSE</span>
          </div>
          <button
            onClick={copyLink}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg text-sm transition-colors"
          >
            {linkCopied ? <Check size={15} className="text-green-400" /> : <Share2 size={15} />}
            {linkCopied ? 'Copied!' : 'Share Playlist'}
          </button>
        </div>
      </header>

      {/* Playlist Hero */}
      <div className="max-w-4xl mx-auto px-4 pt-10 pb-6">
        <div className="flex items-end gap-6">
          {/* Playlist Cover: 2x2 grid of track covers or solid block */}
          <div className="w-40 h-40 rounded-xl overflow-hidden flex-shrink-0 bg-gray-900 border border-white/10 grid grid-cols-2 grid-rows-2">
            {[0,1,2,3].map(i => (
              tracks[i]?.cover_url ? (
                <img key={i} src={`${API_URL}${tracks[i].cover_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div key={i} className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                  <Music size={16} className="text-gray-600" />
                </div>
              )
            ))}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Playlist</p>
            <h1 className="text-4xl font-black text-white mb-2">{playlist.name}</h1>
            {playlist.description && <p className="text-gray-400 text-sm mb-3">{playlist.description}</p>}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{playlist.creator_name}</span>
              <span>·</span>
              <span>{tracks.length} track{tracks.length !== 1 ? 's' : ''}</span>
              {totalDuration > 0 && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock size={13} />{formatTime(totalDuration)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Play All Button */}
        {tracks.length > 0 && (
          <button
            onClick={() => { setCurrentTrack(tracks[0]); setIsPlaying(true); }}
            className="mt-6 flex items-center gap-3 px-8 py-3 bg-white text-black rounded-full font-bold text-sm hover:bg-gray-200 transition-colors"
          >
            <Play size={18} fill="black" />
            Play All
          </button>
        )}
      </div>

      {/* Track List */}
      <div className="max-w-4xl mx-auto px-4 pb-32">
        {tracks.length === 0 ? (
          <div className="text-center py-16">
            <Music size={48} className="mx-auto text-gray-700 mb-3" />
            <p className="text-gray-500">This playlist is empty</p>
          </div>
        ) : (
          <div className="space-y-1">
            {tracks.map((track, index) => (
              <div
                key={track.id}
                onClick={() => playTrack(track)}
                className={`group flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all ${
                  currentTrack?.id === track.id
                    ? 'bg-white/10 border border-white/30'
                    : 'hover:bg-white/5 border border-transparent'
                }`}
              >
                {/* Index / Play indicator */}
                <div className="w-8 text-center flex-shrink-0">
                  {currentTrack?.id === track.id && isPlaying ? (
                    <Pause size={16} className="mx-auto text-white" />
                  ) : (
                    <>
                      <span className="text-gray-500 text-sm group-hover:hidden">{index + 1}</span>
                      <Play size={16} className="mx-auto text-white hidden group-hover:block" fill="white" />
                    </>
                  )}
                </div>

                {/* Cover */}
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800">
                  {track.cover_url ? (
                    <img src={`${API_URL}${track.cover_url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                      <Music size={18} className="text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium truncate ${currentTrack?.id === track.id ? 'text-white' : 'text-gray-200'}`}>{track.title}</p>
                  <p className="text-sm text-gray-500 truncate">{track.artist || 'Unknown Artist'}</p>
                </div>

                {/* Metadata */}
                <div className="hidden md:flex items-center gap-2 text-xs text-gray-500">
                  {track.genre && <span className="px-2 py-1 bg-white/5 rounded">{track.genre}</span>}
                  {track.bpm && <span className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded"><Activity size={11} />{track.bpm}</span>}
                  {track.key && <span className="px-2 py-1 bg-white/5 rounded">{track.key}</span>}
                </div>

                {/* Duration */}
                {track.duration && (
                  <span className="text-xs text-gray-500 flex-shrink-0">{formatTime(track.duration)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fixed Player Bar */}
      {currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-950 border-t border-gray-800 z-50">
          <div className="h-0.5 bg-gray-800 cursor-pointer" onClick={e => {
            const rect = e.currentTarget.getBoundingClientRect();
            if (audioRef.current) audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
          }}>
            <div className="h-full bg-white transition-all" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
          </div>
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
            {/* Track info */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800">
                {currentTrack.cover_url ? (
                  <img src={`${API_URL}${currentTrack.cover_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                    <Music size={16} className="text-gray-400" />
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-medium text-white text-sm truncate">{currentTrack.title}</p>
                <p className="text-xs text-gray-400 truncate">{currentTrack.artist}</p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3">
              <button onClick={playPrev} className="p-2 text-gray-400 hover:text-white transition-colors"><SkipBack size={20} /></button>
              <button
                onClick={() => { if (isPlaying) { audioRef.current?.pause(); } else { audioRef.current?.play(); } setIsPlaying(!isPlaying); }}
                className="w-11 h-11 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                {isPlaying ? <Pause size={20} className="text-black" /> : <Play size={20} className="text-black" fill="black" />}
              </button>
              <button onClick={playNext} className="p-2 text-gray-400 hover:text-white transition-colors"><SkipForward size={20} /></button>
            </div>

            {/* Time + Volume */}
            <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
              <span className="text-xs text-gray-400">{formatTime(currentTime)} / {formatTime(duration)}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => { const m = !isMuted; setIsMuted(m); if (audioRef.current) audioRef.current.volume = m ? 0 : volume; }}
                  className="p-1.5 text-gray-400 hover:text-white">
                  {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume}
                  onChange={e => { const v = parseFloat(e.target.value); setVolume(v); setIsMuted(v === 0); if (audioRef.current) audioRef.current.volume = v; }}
                  className="w-20 accent-white" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPlaylist;
